import { ECOLAB_API_URL, STORAGE } from '@/config/authConfig';

/**
 * Cliente de la API de autenticación de Ecolab.
 *
 * Deliberadamente NO usa el axios del POS: ese lleva interceptores que
 * inyectan el token del POS y manejan 401 redirigiendo a /login. Llamar a
 * Ecolab con ellos produciría bucles. Aquí se usa fetch pelado.
 *
 * El contrato es el mismo que consume b2b_frontend (Marketplace):
 *   GET  /api/ecolab/auth/turnstile-sitekey  → { siteKey }   (público)
 *   POST /api/ecolab/auth/login    { email, password, turnstileToken }
 *   POST /api/ecolab/auth/refresh  { refreshToken }
 *
 * Respuesta:
 *   { accessToken, refreshToken, expiresIn, usuario: { id, email, nombre, apellido, roles } }
 *
 * Los nombres del token varían entre versiones (accessToken / access_token /
 * token / jwt), así que se cubren los cuatro — igual que hace el Marketplace.
 */

export interface EcolabUsuario {
  id: string;
  email: string;
  nombre?: string;
  apellido?: string;
  roles?: string[];
  perfilId?: number;
}

export interface EcolabLoginResult {
  accessToken: string;
  refreshToken?: string;
  usuario: EcolabUsuario;
}

/** Claims del JWT que emite Ecolab (TokenServiceEcolab). */
interface EcolabJwtPayload {
  nameid?: string;
  sub?: string;
  email?: string;
  name?: string;
  family_name?: string;
  unique_name?: string;
  perfilId?: string;
  TipoEntidad?: string;
  EntidadID?: string;
  role?: string | string[];
  exp?: number;
}

/**
 * Decodifica el payload del JWT sin verificar la firma.
 * Solo para leer datos de presentación — la validación real la hace el backend.
 */
export const decodificarJwt = (jwt: string): EcolabJwtPayload => {
  try {
    const base64 = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return {};
  }
};

const extraerToken = (data: any): string | undefined =>
  data?.accessToken || data?.access_token || data?.token || data?.jwt;

/** Error del login de Ecolab; turnstileError indica que falló la verificación de Cloudflare. */
export class EcolabAuthError extends Error {
  constructor(message: string, public turnstileError = false) {
    super(message);
    this.name = 'EcolabAuthError';
  }
}

export const ecolabAuthService = {
  /** Site key de Turnstile (pública). Ecolab la entrega para no dejarla fija en el frontend. */
  obtenerTurnstileSiteKey: async (): Promise<string> => {
    const resp = await fetch(`${ECOLAB_API_URL}/api/ecolab/auth/turnstile-sitekey`);
    if (!resp.ok) throw new EcolabAuthError('No se pudo obtener la configuración de seguridad.');
    const data = await resp.json();
    if (!data?.siteKey) throw new EcolabAuthError('No se pudo obtener la configuración de seguridad.');
    return data.siteKey;
  },

  login: async (email: string, password: string, turnstileToken: string): Promise<EcolabLoginResult> => {
    let resp: Response;
    try {
      resp = await fetch(`${ECOLAB_API_URL}/api/ecolab/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, turnstileToken }),
      });
    } catch {
      throw new EcolabAuthError('No se pudo conectar con el servidor de autenticación. Verifica tu conexión.');
    }

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({} as any));
      throw new EcolabAuthError(
        err?.mensaje || err?.message ||
          (resp.status === 401
            ? 'Correo o contraseña incorrectos'
            : `Error ${resp.status} al iniciar sesión`),
        Boolean(err?.turnstileError)
      );
    }

    const data = await resp.json();
    const accessToken = extraerToken(data);

    if (!accessToken) {
      // Ecolab respondió 200 pero sin JWT reconocible. Fallar aquí con un
      // mensaje claro es mejor que dejar la sesión a medias — sin token,
      // todas las llamadas al POS darían 401 sin explicación.
      // eslint-disable-next-line no-console
      console.error(
        'Login: Ecolab respondió 200 sin JWT. Campos recibidos:',
        Object.keys(data || {})
      );
      throw new Error('El servidor de autenticación respondió sin token.');
    }

    const claims = decodificarJwt(accessToken);
    const u = data.usuario || data.user || {};

    return {
      accessToken,
      refreshToken: data.refreshToken || data.refresh_token,
      usuario: {
        id: u.id || u.aspNetUserId || claims.nameid || claims.sub || '',
        email: u.email || claims.email || email,
        nombre: u.nombre || u.name || claims.name,
        apellido: u.apellido || u.familyName || claims.family_name,
        roles: u.roles || (claims.role
          ? Array.isArray(claims.role) ? claims.role : [claims.role]
          : []),
        perfilId: u.perfilId ?? (claims.perfilId ? Number(claims.perfilId) : undefined),
      },
    };
  },

  /** Renueva el access token. Devuelve null si no se pudo. */
  refresh: async (): Promise<string | null> => {
    const refreshToken = localStorage.getItem(STORAGE.REFRESH);
    if (!refreshToken) return null;

    try {
      const resp = await fetch(`${ECOLAB_API_URL}/api/ecolab/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!resp.ok) return null;

      const data = await resp.json();
      const nuevo = extraerToken(data);
      if (!nuevo) return null;

      localStorage.setItem(STORAGE.TOKEN, nuevo);
      if (data.refreshToken || data.refresh_token) {
        localStorage.setItem(STORAGE.REFRESH, data.refreshToken || data.refresh_token);
      }
      return nuevo;
    } catch {
      return null;
    }
  },
};

export default ecolabAuthService;
